// Types
import type { NavigationLink } from 'layouts/ProfileNavigation/components/NavigationSection/components/NavigationItem/NavigationItem';
import type { IconNames } from 'ui3/MillionsIcon/MillionsIcon';
// Hooks
import {
  useGetCurrUser,
  useOnboardingContext,
  useStreamChatClient,
} from 'hooks';
import { useTicketingLaunched } from 'hooks/useTicketingLaunched';
// Layouts
import CreatorCard from 'layouts/Header/CreatorCard/CreatorCard';
// Constants
import {
  PURCHASES,
  DASHBOARD,
  PROFILE,
  MANAGE_MERCH,
  MANAGE_STREAMS,
  MANAGE_EVENTS,
  EVENT_ANALYTICS,
  ACCOUNT_PRODUCTS,
  MANAGE_EXPERIENCES,
  MEDIA_LIBRARY,
  MANAGE_INTERVIEWS,
  MANAGE_AMA,
  MANAGE_BRAND_DEALS_MESSAGES,
  ACCOUNT_SETTINGS,
  MY_SALES,
  NOTIFICATIONS,
  SPONSORED_CHANNEL,
  AFFILIATE_EARNINGS,
} from 'constants/routes';
import { MY_TICKETS } from 'components/Purchase/purchaseTabs';
// Helpers
import {
  isUserAthlete,
  isOnboardedStore,
  isUserOrganization,
  isUserContentCreator,
  isUserCustomer,
} from 'helpers/user';
// Components
import NavigationSection from 'layouts/ProfileNavigation/components/NavigationSection/NavigationSection';
import ProfileSection from 'layouts/ProfileNavigation/components/ProfileSection/ProfileSection';
// Styles
import styles from './ProfileNavigation.module.scss';

const USER_LINKS: NavigationLink[] = [
  {
    icon: 'userCircleIcon' as IconNames,
    label: 'My Profile',
    href: PROFILE,
  },
  {
    icon: 'dollarSign' as IconNames,
    label: 'Sponsored Channel',
    href: SPONSORED_CHANNEL,
  },
  {
    icon: 'coins' as IconNames,
    label: 'Affiliate Earnings',
    href: AFFILIATE_EARNINGS,
  },
  {
    icon: 'bellIcon' as IconNames,
    label: 'Notifications',
    href: NOTIFICATIONS,
    fillIcon: true,
  },
  {
    icon: 'shoppingCartIcon' as IconNames,
    label: 'Orders',
    href: PURCHASES,
  },
  { icon: 'cogIcon' as IconNames, label: 'Settings', href: ACCOUNT_SETTINGS },
];

const ProfileNavigation = () => {
  const { data } = useGetCurrUser();
  const { setShowOnboardingModal } = useOnboardingContext();
  const { unreadCount: chatUnreadCount } = useStreamChatClient();
  const ticketingLaunched = useTicketingLaunched();

  const isBasicUser = isUserCustomer(data);
  const isAthlete = isUserAthlete(data);
  const isOrganization = isUserOrganization(data);
  const isContentCreator = isUserContentCreator(data);
  const isOnboarded = isOnboardedStore(data);
  const showStoreLinks = isAthlete || isOrganization || isContentCreator;

  const handleOnboardingClick = () => {
    setShowOnboardingModal(true);
  };

  const onboardedLinks: NavigationLink[] = isOnboarded
    ? [
        {
          icon: 'salesIcon' as IconNames,
          label: 'Sales',
          href: MY_SALES,
        },
        ...(!isOrganization
          ? [
              {
                icon: 'priceTagIcon' as IconNames,
                label: 'Brand Deals',
                href: MANAGE_AMA,
              },
            ]
          : []),
        {
          icon: 'microphoneIcon' as IconNames,
          label: 'Interviews',
          href: MANAGE_INTERVIEWS,
        },
        {
          icon: 'streamsIcon' as IconNames,
          label: 'Streams',
          href: MANAGE_STREAMS,
          // Also active on a stream's pages under it, e.g. its Event Analytics.
          activeHrefs: [MANAGE_STREAMS],
        },
        ...(ticketingLaunched
          ? [
              {
                icon: 'ticket' as IconNames,
                label: 'Events',
                href: MANAGE_EVENTS,
                activeHrefs: [EVENT_ANALYTICS],
              },
            ]
          : []),
        {
          icon: 'playIcon' as IconNames,
          label: 'Media Feed',
          href: MEDIA_LIBRARY,
        },
        {
          icon: 'bagIcon' as IconNames,
          label: 'My Shop',
          href: MANAGE_MERCH,
          activeHrefs: [ACCOUNT_PRODUCTS],
        },
        {
          icon: 'starIcon' as IconNames,
          label: 'Experiences',
          href: MANAGE_EXPERIENCES,
        },
      ]
    : [];

  const dashboard: NavigationLink[] = [
    {
      icon: 'statsIcon' as IconNames,
      label: 'Dashboard',
      href: DASHBOARD,
    },
  ];

  // The inbox carries interview and event chats as well as brand deals, so every store
  // user (athlete, organization, content creator) gets it — organizations included,
  // even though they have no Brand Deals. Fans are not store users and never see it.
  const chatLinks: NavigationLink[] = showStoreLinks
    ? [
        {
          icon: 'mail' as IconNames,
          label: 'Chat',
          href: MANAGE_BRAND_DEALS_MESSAGES,
          badgeCount: chatUnreadCount,
        },
      ]
    : [];

  const storeLinks: NavigationLink[] = [
    ...chatLinks,
    ...dashboard,
    ...onboardedLinks,
  ];

  // Sponsored Channel is a creator-side feature — hide it from fans (customers).
  const accountLinks = USER_LINKS.filter(
    (link) => link.href !== SPONSORED_CHANNEL || showStoreLinks
  ).flatMap((link) =>
    link.href === PURCHASES && ticketingLaunched
      ? [
          link,
          {
            icon: 'ticket' as IconNames,
            label: 'My Tickets',
            href: MY_TICKETS,
          },
        ]
      : [link]
  );

  return (
    <aside className={styles.root} data-testid="sideBar">
      <div className={styles.content}>
        {!showStoreLinks && (
          <NavigationSection links={dashboard} title="menu" />
        )}

        {showStoreLinks && (
          <>
            <ProfileSection />
            <NavigationSection links={storeLinks} title="menu" />
          </>
        )}

        <NavigationSection links={accountLinks} title="account" />

        {isBasicUser && <CreatorCard onConfirmClick={handleOnboardingClick} />}
      </div>
    </aside>
  );
};

export default ProfileNavigation;
